import type { Notification } from "hooks/firestore/v2//models/notification";
import { useEffect, useMemo, useRef } from "react";
import { PickemsTransforms } from "selectors";
import { getDefaultBettingPath } from "utilities/sharedBettingUtilities";
import { usePreferences } from "hooks/firestore/v2/user/usePreferences";
import { useSelector } from "hooks";
import useSWRInfinite from "swr/infinite";
import { stringify } from "query-string";
import { useUser } from "hooks/firestore/v2/user/useUser";
import { parseISO } from "date-fns";

export const getLink = (
  objectType: Notification["externalReferenceType"],
  objectId: Notification["externalReferenceId"],
  type: Notification["type"],
  defaultHub: string,
): string => {
  if (objectType === "PICKEMS_CONTEST") {
    return PickemsTransforms.getPlayLink(objectId);
  }

  const bettingPath = getDefaultBettingPath(defaultHub);

  switch (type) {
    case "WELCOME_BETTING":
      return bettingPath;
    case "WELCOME_FANTASY":
    case "WELCOME":
      return "/pickems";
    case "VERIFICATION_SUCCESS":
      return "/wallet/deposit";
    case "VERIFICATION_REMINDER":
    case "VERIFICATION_REMINDER_IOM":
      return "/account/verification";
    case "DEPOSIT":
    case "ADMIN_DEPOSIT":
    case "DEPOSIT_FAILED":
    case "DEPOSIT_REFUND":
    case "WITHDRAWAL_PROCESSED":
    case "WITHDRAWAL_DECLINED":
    case "WITHDRAWAL_PARTIALLY_DECLINED":
    case "SKRILL_WITHDRAWAL_CANCELLED":
    case "PROMOTION_PAID":
      return `/wallet/statement`;
    case "PROMOTION_CLAIM_FAILED":
      return "/bonus-offers";
    case "BET_CANCELLED":
    case "BET_WON":
    case "PROMOTION_BET_STAKE_REFUND":
      return "/entries/historical";
    case "BET_ROLLBACK":
    case "BET_LEG_VOIDED":
    case "BET_LEG_UNVOIDED":
      return "/entries/live";
    case "VERIFY_EMAIL":
      return "/account/verification";
    case "DEPOSIT_LIMIT_REMINDER":
    case "DEPOSIT_LIMIT_RAISED":
    case "DEPOSIT_LIMIT_RAISE_REQUEST_RECEIVED":
      return "/preferences/responsible-gambling";
    case "EMAIL_UNSUBSCRIBED":
      return "/account/notifications";
    case "CREDIT_CARD_VERIFICATION_REQUIRED":
      return `/wallet/withdraw`;
    default:
      return bettingPath;
  }
};

type NotificationType = {
  id: string;
  message: string;
  date: Date;
  isRead: boolean;
  link: string;
  externalType: string;
  externalId: string;
  type: string;
};

type NotificationsResponse = {
  query: {
    conditionType?: string;
    limit: number;
    offset?: number;
    skipCount?: boolean;
    sortBy?: string;
    sortOrder?: string;
  };
  result: Array<Notification>;
  totalEntities: number;
};

const useNotifications = (
  limit: number, // number of rows to load
) => {
  const { userId } = useSelector((state) => state.auth);
  const { preferences } = usePreferences();
  const undismissedNotificationsCount = useUndismissedNotificationsCount();
  const hasBeenRendered = useRef(false);
  // Fetch an extra row to find out if there are more pages
  const actualLimit = limit + 1;

  const getKey = (
    pageIndex: number,
    previousPageData: NotificationsResponse,
  ) => {
    const path = `${process.env.GATSBY_API_URI}/notifications`;
    const params: {
      limit: number;
      sortOrder?: string;
      offset?: number;
    } = {
      limit: actualLimit,
      sortOrder: "DESC",
    };

    if (!userId) return null;

    // Reach the end
    if (
      previousPageData &&
      previousPageData.result &&
      previousPageData.result.length < actualLimit
    )
      return null;

    if (pageIndex > 0) {
      params.offset = pageIndex * limit;
    }
    return `${path}?${stringify(params)}`;
  };

  const {
    data: pages,
    error,
    size,
    setSize,
    isLoading,
    mutate: fetchData,
  } = useSWRInfinite(getKey, { revalidateFirstPage: false }); // we fetchData when there's new one

  const notifications = useMemo(() => {
    if (isLoading || !pages) return [];

    const flattened = pages.flatMap((page) => {
      const result = page?.result || [];
      // Remove last record from each page to avoid duplicates unless it's on the last page
      return result.length === actualLimit ? result.slice(0, -1) : result;
    });

    return flattened?.map((notification: Notification) => {
      return {
        date:
          typeof notification.createdAt === "string"
            ? parseISO(notification.createdAt)
            : notification.createdAt.toDate(),
        externalType: notification.externalReferenceType,
        externalId: notification.externalReferenceId,
        id: notification.id,
        isRead: Boolean(notification.dismissed),
        link: getLink(
          notification.externalReferenceType,
          notification.externalReferenceId,
          notification.type,
          preferences?.defaultHub?.toLowerCase(),
        ),
        message: notification.message,
        type: notification.type,
      };
    });
  }, [pages, preferences, isLoading]);

  const hasMore =
    !!notifications &&
    notifications.length > 0 &&
    notifications.length % limit === 0;

  const loadMore = () => {
    setSize(size + 1);
  };

  useEffect(() => {
    if (undismissedNotificationsCount === undefined) return;

    if (!hasBeenRendered.current) {
      hasBeenRendered.current = true;
      return;
    }

    // After first render, fetch when undismissedNotificationsCount changes
    fetchData();
  }, [undismissedNotificationsCount]);

  return {
    notifications,
    loading: isLoading && !error,
    fetchData,
    loadMore,
    hasMore,
  };
};

const useUndismissedNotificationsCount = () => {
  const { user } = useUser();
  return user?.notificationsSummary?.undismissedCount;
};

type Notifications = Exclude<
  ReturnType<typeof useNotifications>["notifications"],
  undefined
>;

export {
  type NotificationType,
  useNotifications,
  useUndismissedNotificationsCount,
  type Notifications,
};
